import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { OrganizationStructuredData } from '../components/StructuredData';

const AboutPage: React.FC = () => {
  return (
    <div className="bg-gray-50 py-12">
      <SEO 
        title="About GoodCart"
        description="GoodCart helps you shop smarter with vetted, health-conscious products and clean ingredients. Learn about our mission, values, and how we curate products."
        image="/og-image.png"
        imageAlt="About GoodCart - Our mission and values"
        url="https://goodcart.org/about"
        type="website"
        locale="en_US"
        siteName="GoodCart"
      />
      
      <OrganizationStructuredData />
      
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-6">About GoodCart</h1>
            
            <div className="bg-white rounded-lg shadow-md p-8 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 mb-6">
                GoodCart helps you shop smarter with vetted, health-conscious products chosen for clean ingredients. 
                We curate items based on publicly available health ratings to save you time and simplify your shopping experience.
              </p>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h2>
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-brand-green-500 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    <strong>Browse categories</strong> of pre-vetted products known for clean ingredients
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-brand-green-500 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    <strong>See products organized</strong> by health ratings from public sources
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-brand-green-500 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    <strong>Purchase directly</strong> through our partner links
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-brand-green-500 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    <strong>Enjoy a streamlined shopping experience</strong> for health-conscious products
                  </p>
                </div>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Our Values</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Transparency</h3>
                  <p className="text-gray-700 text-sm">
                    We're upfront about our business model and how we curate products.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Convenience</h3>
                  <p className="text-gray-700 text-sm">
                    We make it easy to find and purchase health-conscious products.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Quality</h3>
                  <p className="text-gray-700 text-sm">
                    We focus on products with clean ingredients and good health ratings.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Accessibility</h3>
                  <p className="text-gray-700 text-sm">
                    We make health information easy to understand and act upon.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-8 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Transparency Statement</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  GoodCart earns a commission when you purchase through our links, at no extra cost to you. 
                  we may receive a commission at no additional cost to you. This helps us maintain and improve our service 
                  while keeping it accessible to everyone.
                </p>
                <p className="text-gray-700">
                  We aggregate publicly available product information to enhance your shopping experience. 
                  Product ratings information comes from public sources and user contributions.
                </p>
                <p className="text-gray-700">
                  GoodCart is an independent service. Product ratings information comes from public sources and user contributions.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-6">
                Have questions, suggestions, or feedback? We'd love to hear from you!
              </p>
              <a 
                href="mailto:contact@goodcart.org" 
                className="btn btn-primary inline-flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;